<template>
  <BaseModal overlay-class="other_user_prof">
    <div class="content">
      <div class="trans_box">
        <div v-if="!fetching" class="flex-row outer">
          <div class="flex-column left">
            <AvatarImage :src="userDetails.avatar" />
          </div>

          <div class="flex-column right">
            <p class="name">
              {{ userDetails.username }}
            </p>

            <div class="flex-row inner inner_trans_box">
              <BaseImage src="/static/images/prizes-leaderboards/points_1.svg" alt="points" />

              <p class="points">{{ userDetails.totalPointsEarned ?? 0 }}</p>

              <p class="lbl">
                {{ $_translations.value.modal_user_profile_stats_points }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <h1 v-if="!fetching">{{ $_translations.value.modal_user_profile_game_performance }}</h1>

      <div
        v-for="statsConfig in statsConfigList"
        :key="statsConfig.id"
        class="flex-row inner inner_trans_box bottom"
      >
        <template v-if="!fetching">
          <BaseImage :src="statsConfig.imageSrc" :alt="statsConfig.imageAlt" />

          <p class="points">{{ statsConfig.value }}</p>

          <p class="lbl">
            {{
              statsConfig.skipTranslateDescription
                ? statsConfig.description
                : $_translations.value[statsConfig.description]
            }}
          </p>
        </template>
      </div>
    </div>
  </BaseModal>
</template>


<script setup>
import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import AvatarImage from '@/components/image/AvatarImage.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import useLevelStore from '@/store/modules/level.js';
import { isPositiveNumber } from '@/helpers/common.js';
import {
  apiRetrieveUserDetails,
  apiRetrieveUserAchievements,
  apiRetrieveUserStatistics,
} from '@/services/user.js';

const { proxy } = getCurrentInstance();

const props = defineProps({
  userMsisdn: {
    type: String,
    default: '',
  },
});

const levelStore = useLevelStore();

const userDetails = ref({
  username: '',
  avatar: '',
  levelNumber: null,
  levelText: '',
  levelImage: '',
  gamesToNextLevel: null,
  totalPointsEarned: null,
  totalGamesPlayed: null,
  totalCorrectQuestions: null,
  totalUnlockedAchievements: null,
});
const fetching = ref(false);

const hasUserCompletedAllLevels = computed(() => levelStore.totalLevels <= userDetails.value.levelNumber);

const statsConfigList = computed(() => [
  {
    id: 'level',
    imageSrc: userDetails.value.levelImage,
    imageAlt: 'level',
    value: userDetails.value.levelText,
    description: (!hasUserCompletedAllLevels.value && isPositiveNumber(userDetails.value.gamesToNextLevel) && [userDetails.value.gamesToNextLevel, proxy.$_translate('modal_user_profile_stats_level')].join(' ')) || '',
    skipTranslateDescription: true,
  },
  {
    id: 'gamesPlayed',
    imageSrc: '/static/images/prizes-leaderboards/games_left_yes.svg',
    imageAlt: 'games',
    value: userDetails.value.totalGamesPlayed ?? 0,
    description: 'modal_user_profile_stats_games',
  },
  {
    id: 'correctAnswers',
    imageSrc: '/static/images/prizes-leaderboards/correct.svg',
    imageAlt: 'answers',
    value: userDetails.value.totalCorrectQuestions ?? 0,
    description: 'modal_user_profile_stats_correct_answers',
  },
  {
    id: 'achievements',
    imageSrc: '/static/images/prizes-leaderboards/achievements.svg',
    imageAlt: 'achievements',
    value: userDetails.value.totalUnlockedAchievements ?? 0,
    description: 'modal_user_profile_stats_achievements',
  },
]);

const getGapToLevelUp = (levelNumber, totalGamesPlayed) => {
  if (hasUserCompletedAllLevels.value) {
    return 0;
  }

  const nextUserLevelNumber = levelNumber + 1;

  const levelThreshold = levelStore.levelList.find(level => level.__level === nextUserLevelNumber)?.threshold;


  return (isPositiveNumber(levelThreshold) && (levelThreshold > totalGamesPlayed))
    ? levelThreshold - totalGamesPlayed
    : 0;
};

const loadUserData = async () => {
  try {
    fetching.value = true;

    const [detailsResponse, statisticsResponse, achievementsResponse] = await Promise.allSettled([
      apiRetrieveUserDetails({ msisdn: props.userMsisdn }),
      apiRetrieveUserStatistics({ msisdn: props.userMsisdn }),
      apiRetrieveUserAchievements({ msisdn: props.userMsisdn }),
    ]);

    if (detailsResponse.status === 'fulfilled') {
      const levelNumber = detailsResponse.value.parsedData.level;
      const totalGamesPlayed = detailsResponse.value.parsedData.gamesPlayed;

      userDetails.value.levelNumber = levelNumber;
      userDetails.value.totalGamesPlayed = totalGamesPlayed;
      userDetails.value.avatar = detailsResponse.value.parsedData.avatar;
      userDetails.value.username = detailsResponse.value.parsedData.username;
      userDetails.value.levelText = [proxy.$_translate('common_level_short'), levelNumber].join(' ');
      userDetails.value.levelImage = `/static/images/prizes-leaderboards/lvl${ levelNumber }.svg`;
      userDetails.value.gamesToNextLevel = getGapToLevelUp(levelNumber, totalGamesPlayed);
      userDetails.value.totalPointsEarned = detailsResponse.value.parsedData.totalPointsEarned;
    }

    if (statisticsResponse.status === 'fulfilled') {
      userDetails.value.totalCorrectQuestions = statisticsResponse.value.parsedData.correctQuestions;
    }

    if (achievementsResponse.status === 'fulfilled') {
      userDetails.value.totalUnlockedAchievements = achievementsResponse.value.parsedData.totalUnlockedAchievements;
    }

    fetching.value = false;
  } catch {
    fetching.value = false;
  }
};

onMounted(loadUserData);
</script>
